import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

interface EligibilityData {
  companyName: string;
  ticker: string;
  marketCap: number;
  avgVolume: number;
  currentPrice: number;
  maxCommitment: number;
  discountRate: number;
  sharesOutstanding: number;
  inquiryId?: string;
  exclusivityInfo?: {
    hasExclusivity: boolean;
    brokerName?: string;
    expiresAt?: string;
    type?: string;
  };
}

interface DealTerms {
  facilitySize: number;
  discountType: 'fixed' | 'variable';
  fixedDiscount: number;
  variableDiscountMin: number;
  variableDiscountMax: number;
  lookbackDays: number;
  termMonths: number;
  commitmentFee: number;
  legalFee: number;
}

interface AdvanceDrawOptions {
  maxAdvanceRate: number;
  minDrawAmount: number;
  maxDrawFrequency: string;
  noticePeriod: number;
  pricingMechanism: 'VWAP' | 'TWAP' | 'Closing';
}

const BrokerEligibility: React.FC = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState<EligibilityData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Deal terms state
  const [dealTerms, setDealTerms] = useState<DealTerms>({
    facilitySize: 0,
    discountType: 'fixed',
    fixedDiscount: 3,
    variableDiscountMin: 2,
    variableDiscountMax: 5,
    lookbackDays: 10,
    termMonths: 24,
    commitmentFee: 10000,
    legalFee: 25000
  });

  // Advance draw options state
  const [advanceOptions, setAdvanceOptions] = useState<AdvanceDrawOptions>({
    maxAdvanceRate: 30,
    minDrawAmount: 100000,
    maxDrawFrequency: 'monthly',
    noticePeriod: 3,
    pricingMechanism: 'VWAP'
  });

  // Origination fee state
  const [originationFee, setOriginationFee] = useState(1.0);

  // Client info for sending terms
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: '',
    company: ''
  });

  useEffect(() => {
    if (symbol) {
      runEligibilityCheck();
    }
  }, [symbol]);

  const runEligibilityCheck = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post('/api/broker/eligibility', {
        symbol: symbol?.toUpperCase(),
        originationFee: originationFee
      });
      
      setEligibility(response.data);
      
      // Set initial facility size based on eligibility
      setDealTerms(prev => ({
        ...prev,
        facilitySize: Math.min(response.data.maxCommitment, 10000000) // Default to $10M or max
      }));
      
      setLoading(false);
    } catch (error: any) {
      console.error('Error running eligibility check:', error);
      setError(error.response?.data?.message || 'Failed to check eligibility');
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDownloadTermSheet = async () => {
    try {
      const response = await axios.post('/api/broker/term-sheet/generate', {
        inquiryId: eligibility?.inquiryId,
        dealTerms,
        advanceOptions,
        originationFee,
        eligibilityData: eligibility
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TermSheet_${symbol}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading term sheet:', error);
      alert('Failed to download term sheet');
    }
  };

  const handleSendToClient = async () => {
    if (!clientInfo.name || !clientInfo.email) {
      alert('Please enter client name and email');
      return;
    }

    try {
      const response = await axios.post('/api/broker/invite', {
        inquiryId: eligibility?.inquiryId,
        clientInfo,
        dealTerms,
        advanceOptions,
        originationFee,
        symbol: symbol?.toUpperCase()
      });

      alert(`Terms sent successfully to ${clientInfo.name} at ${clientInfo.email}`);
      navigate('/broker/dashboard');
    } catch (error: any) {
      console.error('Error sending terms to client:', error);
      alert(error.response?.data?.message || 'Failed to send terms to client');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateBrokerCommission = () => {
    return dealTerms.facilitySize * (originationFee / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking eligibility for {symbol}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Eligibility Check Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/broker/dashboard')}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!eligibility) return null;

  // Check for exclusivity conflicts
  if (eligibility.exclusivityInfo?.hasExclusivity) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <LockClosedIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">Exclusivity Notice</h2>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              This opportunity is currently under exclusivity with <strong>{eligibility.exclusivityInfo.brokerName}</strong>.
            </p>
            <p className="text-sm text-yellow-800 mt-2">
              Exclusivity expires on: <strong>{new Date(eligibility.exclusivityInfo.expiresAt!).toLocaleDateString()}</strong>
            </p>
            <p className="text-sm text-yellow-800 mt-2">
              Type: <strong>{eligibility.exclusivityInfo.type}</strong>
            </p>
          </div>
          <button
            onClick={() => navigate('/broker/dashboard')}
            className="w-full px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Eligibility Results
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Eligibility Confirmed</h2>
              <p className="text-gray-600 mt-2">{eligibility.companyName} qualifies for equity financing</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <BuildingOfficeIcon className="h-8 w-8 text-primary-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Company Details</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-600">Symbol:</span> <span className="font-medium">{eligibility.ticker}</span></p>
                  <p><span className="text-gray-600">Market Cap:</span> <span className="font-medium">{formatCurrency(eligibility.marketCap)}</span></p>
                  <p><span className="text-gray-600">Current Price:</span> <span className="font-medium">${eligibility.currentPrice.toFixed(2)}</span></p>
                  <p><span className="text-gray-600">Avg Volume:</span> <span className="font-medium">{eligibility.avgVolume.toLocaleString()}</span></p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <ChartBarIcon className="h-8 w-8 text-primary-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Financing Capacity</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-600">Max Facility:</span> <span className="font-medium">{formatCurrency(eligibility.maxCommitment)}</span></p>
                  <p><span className="text-gray-600">Suggested Discount:</span> <span className="font-medium">{eligibility.discountRate}%</span></p>
                  <p><span className="text-gray-600">Shares Outstanding:</span> <span className="font-medium">{eligibility.sharesOutstanding.toLocaleString()}</span></p>
                  <p><span className="text-gray-600">10-Day Exclusivity:</span> <span className="font-medium text-green-600">Granted</span></p>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        // Deal Options
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Configure Deal Terms</h2>
              <p className="text-gray-600">Set the facility parameters for your client</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-6">
              {/* Facility Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Size
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={dealTerms.facilitySize}
                    onChange={(e) => setDealTerms({ ...dealTerms, facilitySize: Number(e.target.value) })}
                    max={eligibility.maxCommitment}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Maximum available: {formatCurrency(eligibility.maxCommitment)}
                </p>
              </div>

              {/* Discount Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Type
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="fixed"
                      checked={dealTerms.discountType === 'fixed'}
                      onChange={(e) => setDealTerms({ ...dealTerms, discountType: 'fixed' })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="ml-3">
                      <span className="block font-medium">Fixed Discount</span>
                      <span className="block text-sm text-gray-500">Constant rate</span>
                    </div>
                  </label>
                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="variable"
                      checked={dealTerms.discountType === 'variable'}
                      onChange={(e) => setDealTerms({ ...dealTerms, discountType: 'variable' })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="ml-3">
                      <span className="block font-medium">Variable Discount</span>
                      <span className="block text-sm text-gray-500">Market-based</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Discount Rate(s) */}
              {dealTerms.discountType === 'fixed' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Discount Rate (%)
                  </label>
                  <input
                    type="number"
                    value={dealTerms.fixedDiscount}
                    onChange={(e) => setDealTerms({ ...dealTerms, fixedDiscount: Number(e.target.value) })}
                    min="0"
                    max="10"
                    step="0.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Discount (%)
                    </label>
                    <input
                      type="number"
                      value={dealTerms.variableDiscountMin}
                      onChange={(e) => setDealTerms({ ...dealTerms, variableDiscountMin: Number(e.target.value) })}
                      min="0"
                      max="10"
                      step="0.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Discount (%)
                    </label>
                    <input
                      type="number"
                      value={dealTerms.variableDiscountMax}
                      onChange={(e) => setDealTerms({ ...dealTerms, variableDiscountMax: Number(e.target.value) })}
                      min="0"
                      max="10"
                      step="0.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              )}

              {/* Other Terms */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Term Length (months)
                  </label>
                  <select
                    value={dealTerms.termMonths}
                    onChange={(e) => setDealTerms({ ...dealTerms, termMonths: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                    <option value={36}>36 months</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lookback Days
                  </label>
                  <input
                    type="number"
                    value={dealTerms.lookbackDays}
                    onChange={(e) => setDealTerms({ ...dealTerms, lookbackDays: Number(e.target.value) })}
                    min="5"
                    max="30"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Fees */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commitment Fee
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={dealTerms.commitmentFee}
                      onChange={(e) => setDealTerms({ ...dealTerms, commitmentFee: Number(e.target.value) })}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Legal Fee
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={dealTerms.legalFee}
                      onChange={(e) => setDealTerms({ ...dealTerms, legalFee: Number(e.target.value) })}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        // Advance Draw Options
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Advance Draw Options</h2>
              <p className="text-gray-600">Configure how your client can access funds</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-6">
              {/* Max Advance Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Advance Rate (% of daily volume)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="10"
                    max="50"
                    step="5"
                    value={advanceOptions.maxAdvanceRate}
                    onChange={(e) => setAdvanceOptions({ ...advanceOptions, maxAdvanceRate: Number(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="text-xl font-bold text-primary-600 w-16 text-center">
                    {advanceOptions.maxAdvanceRate}%
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Higher rates may require additional approval
                </p>
              </div>

              {/* Min Draw Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Draw Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={advanceOptions.minDrawAmount}
                    onChange={(e) => setAdvanceOptions({ ...advanceOptions, minDrawAmount: Number(e.target.value) })}
                    step="50000"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Draw Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Draw Frequency
                </label>
                <select
                  value={advanceOptions.maxDrawFrequency}
                  onChange={(e) => setAdvanceOptions({ ...advanceOptions, maxDrawFrequency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              {/* Notice Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notice Period (business days)
                </label>
                <input
                  type="number"
                  value={advanceOptions.noticePeriod}
                  onChange={(e) => setAdvanceOptions({ ...advanceOptions, noticePeriod: Number(e.target.value) })}
                  min="1"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Pricing Mechanism */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pricing Mechanism
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {['VWAP', 'TWAP', 'Closing'].map((mechanism) => (
                    <label key={mechanism} className="relative flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value={mechanism}
                        checked={advanceOptions.pricingMechanism === mechanism}
                        onChange={(e) => setAdvanceOptions({ ...advanceOptions, pricingMechanism: e.target.value as any })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 font-medium">{mechanism}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        // Origination Fee
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Your Origination Fee</h2>
              <p className="text-gray-600">This fee will be deducted from each closing</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Origination Fee (% of facility)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={originationFee}
                    onChange={(e) => setOriginationFee(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-3xl font-bold text-primary-600 w-24 text-center">
                    {originationFee.toFixed(1)}%
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Commission Calculation</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Facility Size:</span>
                    <span className="font-medium">{formatCurrency(dealTerms.facilitySize)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Origination Fee:</span>
                    <span className="font-medium">{originationFee}%</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-lg">
                      <span className="font-medium text-gray-900">Total Commission:</span>
                      <span className="font-bold text-primary-600">{formatCurrency(calculateBrokerCommission())}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> The origination fee will be automatically deducted from each closing and paid directly to your firm.
                </p>
              </div>
            </div>
          </div>
        );

      case 5:
        // Review & Send
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Terms</h2>
              <p className="text-gray-600">Review the complete terms before sending to your client</p>
            </div>

            {/* Terms Summary */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Deal Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Company:</p>
                    <p className="font-medium">{eligibility.companyName} ({eligibility.ticker})</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Facility Size:</p>
                    <p className="font-medium">{formatCurrency(dealTerms.facilitySize)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Discount:</p>
                    <p className="font-medium">
                      {dealTerms.discountType === 'fixed' 
                        ? `${dealTerms.fixedDiscount}% Fixed`
                        : `${dealTerms.variableDiscountMin}%-${dealTerms.variableDiscountMax}% Variable`
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Term:</p>
                    <p className="font-medium">{dealTerms.termMonths} months</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Max Advance Rate:</p>
                    <p className="font-medium">{advanceOptions.maxAdvanceRate}% of volume</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Pricing:</p>
                    <p className="font-medium">{advanceOptions.pricingMechanism}</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Fees</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Commitment Fee:</span>
                    <span className="font-medium">{formatCurrency(dealTerms.commitmentFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Legal Fee:</span>
                    <span className="font-medium">{formatCurrency(dealTerms.legalFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Origination Fee:</span>
                    <span className="font-medium">{originationFee}% ({formatCurrency(calculateBrokerCommission())})</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Download Term Sheet */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <DocumentTextIcon className="h-8 w-8 text-primary-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Download Term Sheet</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Generate and download the term sheet PDF for your records
                </p>
                <button
                  onClick={handleDownloadTermSheet}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Download PDF
                </button>
              </div>

              {/* Send to Client */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <PaperAirplaneIcon className="h-8 w-8 text-primary-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Send to Client</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Email terms with a unique link for client onboarding
                </p>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Client Name"
                    value={clientInfo.name}
                    onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="email"
                    placeholder="Client Email"
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    onClick={handleSendToClient}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                    Send Terms
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Important:</strong> Sending these terms will create a lead in your pipeline and grant you extended 6-month exclusivity on this opportunity.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {['Eligibility', 'Deal Terms', 'Draw Options', 'Origination Fee', 'Review & Send'].map((step, index) => (
                <div
                  key={step}
                  className={`flex items-center ${index < 4 ? 'flex-1' : ''}`}
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    currentStep > index + 1 
                      ? 'bg-green-500 text-white' 
                      : currentStep === index + 1
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {currentStep > index + 1 ? '✓' : index + 1}
                  </div>
                  <span className={`ml-2 text-sm hidden md:inline ${
                    currentStep >= index + 1 ? 'text-gray-900 font-medium' : 'text-gray-500'
                  }`}>
                    {step}
                  </span>
                  {index < 4 && (
                    <div className={`flex-1 h-1 mx-4 ${
                      currentStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                  currentStep === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <ArrowLeftIcon className="h-5 w-5" />
                Back
              </button>

              {currentStep < 5 && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Next
                  <ArrowRightIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BrokerEligibility; 